import { forwardRef } from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"

const menuVariants = cva(
  "flex flex-col w-full bg-neutral-1 border border-neutral-5 rounded-md shadow-sm",
  {
    variants: {
      orientation: {
        vertical: "flex-col",
        horizontal: "flex-row",
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  }
)

const menuItemVariants = cva(
  "flex items-center px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-misc-focus cursor-pointer",
  {
    variants: {
      state: {
        inactive: "text-neutral-12 hover:bg-neutral-3 hover:text-neutral-12",
        active: "text-primary-3 bg-primary-8 hover:bg-primary-7",
        disabled: "text-neutral-6 cursor-not-allowed pointer-events-none",
      },
    },
    defaultVariants: {
      state: "inactive",
    },
  }
)

const Menu = forwardRef(
  ({ className, orientation, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(menuVariants({ orientation }), className)}
      role="menu"
      {...props}
    />
  )
)
Menu.displayName = "Menu"

const MenuItem = forwardRef(
  ({ className, state, asChild = false, ...props }, ref) => {
    const Comp = asChild ? "div" : "div"
    return (
      <Comp
        ref={ref}
        className={cn(menuItemVariants({ state }), className)}
        role="menuitem"
        tabIndex={state === "disabled" ? -1 : 0}
        {...props}
      />
    )
  }
)
MenuItem.displayName = "MenuItem"

const MenuSeparator = forwardRef(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("h-px bg-neutral-5 my-1", className)}
      role="separator"
      {...props}
    />
  )
)
MenuSeparator.displayName = "MenuSeparator"

export { Menu, MenuItem, MenuSeparator, menuVariants, menuItemVariants }
